import { useTheme } from "@mui/material";
import { createSxStyles } from "@common/createSxStyles";

// The panel is resizable, so rows respond to the width of the form rather than to the viewport.
const narrowFormQuery = "@container activityForm (max-width: 480px)";
// A date shown as a relative date needs more room than the other fields before it reads.
const narrowScheduleQuery = "@container activityForm (max-width: 720px)";
const veryNarrowFormQuery = "@container activityForm (max-width: 300px)";

export const useActivityFormStyles = () => {
    const theme = useTheme();

    return createSxStyles({
        form: {
            containerType: "inline-size",
            containerName: "activityForm",
        },
        twoColumns: {
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            columnGap: theme.spacing(2),
            [narrowFormQuery]: {
                gridTemplateColumns: "minmax(0, 1fr)",
            },
        },
        scheduleColumns: {
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            columnGap: theme.spacing(2),
            [narrowScheduleQuery]: {
                gridTemplateColumns: "minmax(0, 1fr)",
            },
        },
        idAndTitle: {
            display: "grid",
            gridTemplateColumns: "minmax(64px, 90px) minmax(0, 1fr)",
            columnGap: theme.spacing(2),
            [veryNarrowFormQuery]: {
                gridTemplateColumns: "minmax(0, 1fr)",
            },
        },
        boxContent: {
            padding: theme.spacing(2),
        },
        // A field draws its fixed label below a top margin of its own, which on top of the box padding leaves the
        // first label further from the border than the content is from the other edges.
        boxContentStartingWithFieldLabel: {
            paddingTop: theme.spacing(1),
        },
        // Fields stacked in a plain block would have their vertical margins collapse into each other; a grid keeps
        // them adding up, like the rows in the other sections.
        singleColumn: {
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr)",
        },
        priorityOption: {
            display: "flex",
            alignItems: "center",
            gap: "12px",
        },
        fullRow: {
            gridColumn: "1 / -1",
        },
        hint: {
            fontSize: theme.typography.pxToRem(12),
            color: theme.palette.grey[600],
        },
        error: {
            fontSize: theme.typography.pxToRem(12),
            color: theme.palette.error.main,
        },
        interval: {
            display: "flex",
            gap: theme.spacing(1),
            "& > :first-of-type": {
                width: 130,
                flexShrink: 0,
            },
            "& > :last-of-type": {
                flex: 1,
                minWidth: 0,
            },
            mt: 1,
        },
        // The checkbox stands in for the end date's label, so its text reads like the field labels beside it.
        // The checkbox has no label placement of its own, and the label has to lead so it lines up with the
        // other field labels. Reversing the row keeps the label clickable.
        endDateCheckbox: {
            "& .MuiFormControlLabel-root": {
                flexDirection: "row-reverse",
                justifyContent: "flex-end",
            },
        },
        endDateCheckboxLabel: {
            fontWeight: 600,
            lineHeight: 1.4,
        },
        // Drawn like an empty, disabled field, so that no end date reads as a choice with a consequence.
        endDatePlaceholder: {
            display: "flex",
            alignItems: "center",
            boxSizing: "border-box",
            height: 38,
            paddingX: "14px",
            border: `1px dashed ${theme.palette.grey.A400}`,
            borderRadius: "10px",
            backgroundColor: theme.palette.grey[50],
            color: theme.palette.text.disabled,
            fontSize: theme.typography.pxToRem(14),
        },
        reminder: {
            maxWidth: 280,
        },
        groupCompanies: {
            marginBottom: theme.spacing(2),
        },
        frequency: {
            marginBottom: theme.spacing(2),
        },
    });
};
